import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, Menu, X, GraduationCap, ChevronDown, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NAV = [
  { label: 'Colleges', to: '/colleges', children: ['Engineering', 'Management', 'Medical', 'Law', 'Design', 'Arts'] },
  { label: 'Universities', to: '/universities' },
  { label: 'Courses', to: '/courses', children: ['B.Tech', 'MBA', 'MBBS', 'B.Des', 'LLB', 'B.Com'] },
  { label: 'Exams', to: '/exams', children: ['JEE Main', 'NEET', 'CAT', 'CLAT', 'GATE'] },
  { label: 'Blog', to: '/blog' },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/colleges?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
      {/* Top strip */}
      <div className="bg-slate-900 text-slate-300 text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <span className="hidden sm:inline">India&apos;s trusted platform for college &amp; exam guidance</span>
          <div className="flex items-center gap-4">
            <a href="tel:+911800123456" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" /> 1800-123-456
            </a>
            <Link to="/admin" className="hover:text-white transition-colors">Admin</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg bg-teal-600 grid place-items-center text-white shadow-sm group-hover:shadow-md transition-shadow">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-slate-900 text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Learn<span className="text-teal-600">dunia</span>
            </div>
            <div className="text-[10px] text-slate-500 tracking-widest uppercase">Learn. Compare. Choose.</div>
          </div>
        </Link>

        {/* Search */}
        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xl mx-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search colleges, courses, exams..."
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm transition-all"
            />
          </div>
        </form>

        {/* Nav (desktop) */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) =>
            n.children ? (
              <DropdownMenu key={n.label}>
                <DropdownMenuTrigger asChild>
                  <button className="px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-teal-700 hover:bg-teal-50 flex items-center gap-1 transition-colors">
                    {n.label} <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to={n.to} className="font-semibold text-teal-700">All {n.label} →</Link>
                  </DropdownMenuItem>
                  {n.children.map((c) => (
                    <DropdownMenuItem asChild key={c}>
                      <Link to={n.to}>{c}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={n.label}
                to={n.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'text-teal-700 bg-teal-50' : 'text-slate-700 hover:text-teal-700 hover:bg-teal-50'
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">Login</Button>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm">Apply Now</Button>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setOpen(!open)} className="lg:hidden ml-auto p-2 rounded-md hover:bg-slate-100" aria-label="Toggle menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            <form onSubmit={submit} className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-teal-500 outline-none text-sm"
                />
              </div>
            </form>
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-teal-50 hover:text-teal-700"
              >
                {n.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1">Login</Button>
              <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">Apply</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
